import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/services/api";
import { stripeService } from "@/services/stripe";
import {
  Server,
  Key,
  RefreshCw,
  Copy,
  Eye,
  EyeOff,
  FileText,
  Settings as SettingsIcon,
  Crown,
  Check,
  AlertCircle,
  CheckCircle,
  Clock,
  UserPlus,
  Trash2,
} from "lucide-react";
import { SftpCredentials } from "@/types/sftp";

interface SftpCredentialsState extends SftpCredentials {
  createdAt: string;
  lastLogin?: string;
  isActive: boolean;
}

interface UserSettings {
  ediDocumentTypes: {
    EDI204: boolean;
    EDI211: boolean;
  };
  x12Version: "4010" | "5010" | "7010";
  timezone: string;
  reviewBeforeEdiEnabled: boolean;
}

// (Removed default interchange ID cookie helpers)

export function Settings() {
  const [sftpCredentials, setSftpCredentials] =
    useState<SftpCredentialsState | null>(null);
  const [savedSenderIds, setSavedSenderIds] = useState<
    { label: string; interchangeId: string }[]
  >([]);
  const [savedReceiverIds, setSavedReceiverIds] = useState<
    { label: string; interchangeId: string }[]
  >([]);
  const [newSenderLabel, setNewSenderLabel] = useState("");
  const [newSenderId, setNewSenderId] = useState("");
  const [newReceiverLabel, setNewReceiverLabel] = useState("");
  const [newReceiverId, setNewReceiverId] = useState("");
  const [userSettings, setUserSettings] = useState<UserSettings>({
    ediDocumentTypes: {
      EDI204: true,
      EDI211: true,
    },
    x12Version: "5010",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    reviewBeforeEdiEnabled: false,
  });
  const [pricingPlan, setPricingPlan] = useState<
    "good-as-a-human" | "better-than-a-human" | "standard"
  >("good-as-a-human");
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [justCreatedKey, setJustCreatedKey] = useState<string | null>(null);

  const { toast } = useToast();

  const compareByLabelThenId = (
    a: { label: string; interchangeId: string },
    b: { label: string; interchangeId: string }
  ) => {
    const la = a.label.toLowerCase();
    const lb = b.label.toLowerCase();
    if (la < lb) {
      return -1;
    }
    if (la > lb) {
      return 1;
    }
    const ia = a.interchangeId.toLowerCase();
    const ib = b.interchangeId.toLowerCase();
    if (ia < ib) {
      return -1;
    }
    if (ia > ib) {
      return 1;
    }
    return 0;
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);

      // Load SFTP credentials, user settings, pricing plan, and user creation status
      const [
        credentialsResponse,
        settingsResponse,
        pricingPlanResponse,
        creationStatusResponse,
      ] = await Promise.all([
        api.getSftpCredentials().catch(() => ({ success: false, data: null })),
        api.getUserSettings(),
        stripeService.getPricingPlan().catch(() => "good-as-a-human"),
        api
          .getSftpUserCreationStatus()
          .catch(() => ({ success: false, data: null })),
      ]);

      if (credentialsResponse.success && credentialsResponse.data) {
        setSftpCredentials(credentialsResponse.data);
      } else {
        setSftpCredentials(null);
      }

      if (settingsResponse.success && settingsResponse.data) {
        // Load from API first, then merge with localStorage timezone if available
        const apiSettings = settingsResponse.data;

        // Map backend camelCase response to frontend structure (API uses camelCase naming policy)
        const frontendSettings: UserSettings = {
          ediDocumentTypes: {
            EDI204: apiSettings.ediDocumentTypes?.edi204 ?? true,
            EDI211: apiSettings.ediDocumentTypes?.edi211 ?? true,
          },
          x12Version: apiSettings.x12Version || "5010",
          timezone:
            apiSettings.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
          reviewBeforeEdiEnabled: Boolean(apiSettings.reviewBeforeEdiEnabled),
        };

        try {
          const localSettings = localStorage.getItem("userSettings");
          if (localSettings) {
            const parsedLocal = JSON.parse(localSettings);
            // Merge API settings with local timezone preference
            setUserSettings({
              ...frontendSettings,
              timezone: parsedLocal.timezone || frontendSettings.timezone,
            });
          } else {
            setUserSettings(frontendSettings);
          }
        } catch (error) {
          setUserSettings(frontendSettings);
        }
      } else {
        toast({
          title: "Error",
          description:
            settingsResponse.message || "Failed to load user settings",
          variant: "destructive",
        });
      }

      // Set pricing plan
      if (pricingPlanResponse && typeof pricingPlanResponse === "string") {
        setPricingPlan(
          pricingPlanResponse as
            | "good-as-a-human"
            | "better-than-a-human"
            | "standard"
        );
      } else {
        setPricingPlan("good-as-a-human");
      }

      // Load API keys
      try {
        const keys = await api.listApiKeys();
        if (keys.success) {
          setApiKeys(keys.data || []);
        }
      } catch {}

      // Load saved/labeled interchange IDs
      try {
        const ids = await api.getSavedInterchangeIds();
        if (ids.success) {
          const senderList = (ids.data?.savedSenderIds || [])
            .slice()
            .sort(compareByLabelThenId);
          const receiverList = (ids.data?.savedReceiverIds || [])
            .slice()
            .sort(compareByLabelThenId);
          setSavedSenderIds(senderList);
          setSavedReceiverIds(receiverList);
        }
      } catch {}
    } catch (error) {
      console.error("Error loading settings:", error);
      toast({
        title: "Error",
        description: `Failed to load settings: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSenderId = async () => {
    if (!newSenderLabel.trim() || !newSenderId.trim()) {
      toast({
        title: "Error",
        description: "Label and ID are required",
        variant: "destructive",
      });
      return;
    }
    try {
      const id = newSenderId.slice(0, 15);
      const res = await api.addSavedSenderId(newSenderLabel.trim(), id);
      if (res.success) {
        const sorted = (res.data || []).slice().sort(compareByLabelThenId);
        setSavedSenderIds(sorted);
        setNewSenderLabel("");
        setNewSenderId("");
      } else {
        toast({
          title: "Error",
          description: res.message || "Failed to add sender ID",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to add sender ID",
        variant: "destructive",
      });
    }
  };

  const handleDeleteSenderId = async (id: string) => {
    try {
      const res = await api.deleteSavedSenderId(id);
      if (res.success) {
        const sorted = (res.data || []).slice().sort(compareByLabelThenId);
        setSavedSenderIds(sorted);
      } else {
        toast({
          title: "Error",
          description: res.message || "Failed to delete sender ID",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to delete sender ID",
        variant: "destructive",
      });
    }
  };

  const handleAddReceiverId = async () => {
    if (!newReceiverLabel.trim() || !newReceiverId.trim()) {
      toast({
        title: "Error",
        description: "Label and ID are required",
        variant: "destructive",
      });
      return;
    }
    try {
      const id = newReceiverId.slice(0, 15);
      const res = await api.addSavedReceiverId(newReceiverLabel.trim(), id);
      if (res.success) {
        const sorted = (res.data || []).slice().sort(compareByLabelThenId);
        setSavedReceiverIds(sorted);
        setNewReceiverLabel("");
        setNewReceiverId("");
      } else {
        toast({
          title: "Error",
          description: res.message || "Failed to add receiver ID",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to add receiver ID",
        variant: "destructive",
      });
    }
  };

  const handleDeleteReceiverId = async (id: string) => {
    try {
      const res = await api.deleteSavedReceiverId(id);
      if (res.success) {
        const sorted = (res.data || []).slice().sort(compareByLabelThenId);
        setSavedReceiverIds(sorted);
      } else {
        toast({
          title: "Error",
          description: res.message || "Failed to delete receiver ID",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to delete receiver ID",
        variant: "destructive",
      });
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast({
        title: "Error",
        description: "Key name is required",
        variant: "destructive",
      });
      return;
    }
    try {
      setCreatingKey(true);
      const res = await api.createApiKey(newKeyName);
      if (res.success) {
        const apiKey = res.data?.apiKey;
        if (apiKey) {
          setJustCreatedKey(apiKey);
        }
        setNewKeyName("");
        const keys = await api.listApiKeys();
        if (keys.success) {
          setApiKeys(keys.data || []);
        }
      } else {
        toast({
          title: "Error",
          description: res.message || "Failed to create API key",
          variant: "destructive",
        });
      }
    } catch (e) {
      toast({
        title: "Error",
        description: "Failed to create API key",
        variant: "destructive",
      });
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeApiKey = async (id: string) => {
    try {
      const res = await api.revokeApiKey(id);
      if (!res.success) {
        toast({
          title: "Error",
          description: res.message || "Failed to revoke key",
          variant: "destructive",
        });
      }
      const keys = await api.listApiKeys();
      if (keys.success) {
        setApiKeys(keys.data || []);
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to revoke key",
        variant: "destructive",
      });
    }
  };

  const handleRotateApiKey = async (id: string) => {
    try {
      const res = await api.rotateApiKey(id);
      if (res.success) {
        const apiKey = res.data?.apiKey;
        if (apiKey) {
          setJustCreatedKey(apiKey);
        }
        const keys = await api.listApiKeys();
        if (keys.success) {
          setApiKeys(keys.data || []);
        }
      } else {
        toast({
          title: "Error",
          description: res.message || "Failed to rotate key",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to rotate key",
        variant: "destructive",
      });
    }
  };

  const handleCreateSftpUser = async () => {
    try {
      setCreatingUser(true);
      const response = await api.createSftpUser(""); // Email will be extracted from token

      if (response.success) {
        toast({
          title: "Success",
          description: "SFTP user created successfully",
        });
        // Reload settings to get new credentials
        await loadSettings();
      } else {
        toast({
          title: "Error",
          description:
            response.data?.errorMessage || "Failed to create SFTP user",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error creating SFTP user:", error);
      toast({
        title: "Error",
        description: "Failed to create SFTP user",
        variant: "destructive",
      });
    } finally {
      setCreatingUser(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword.trim()) {
      toast({
        title: "Error",
        description: "Please enter a new password",
        variant: "destructive",
      });
      return;
    }

    try {
      setUpdating(true);
      const response = await api.resetSftpPassword("", newPassword); // Email will be extracted from token

      if (response.success) {
        setSftpCredentials((prev) =>
          prev
            ? {
                ...prev,
                password: newPassword,
              }
            : null
        );
        setNewPassword("");
        toast({
          title: "Success",
          description: "Password updated successfully",
        });
      } else {
        toast({
          title: "Error",
          description:
            response.data?.errorMessage || "Failed to update password",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating password:", error);
      toast({
        title: "Error",
        description: "Failed to update password",
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setUpdating(true);

      // Save timezone to localStorage for immediate use
      localStorage.setItem("userSettings", JSON.stringify(userSettings));

      // Map frontend to backend - JsonNamingPolicy.CamelCase converts PascalCase properties to camelCase
      const backendSettings = {
        EdiDocumentTypes: {
          EDI204: userSettings.ediDocumentTypes.EDI204,
          EDI211: userSettings.ediDocumentTypes.EDI211,
        },
        X12Version: userSettings.x12Version,
        Timezone: userSettings.timezone,
        ReviewBeforeEdiEnabled: userSettings.reviewBeforeEdiEnabled,
      };

      const response = await api.updateUserSettings(backendSettings);

      if (response.success) {
        toast({
          title: "Success",
          description: "Settings saved successfully",
        });

        // No default interchange ID cookies
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleSavePricingPlan = async () => {
    try {
      setUpdating(true);

      await stripeService.updatePricingPlan(pricingPlan);

      toast({
        title: "Success",
        description: "Pricing plan updated successfully",
      });
    } catch (error) {
      console.error("Error saving pricing plan:", error);
      toast({
        title: "Error",
        description: "Failed to update pricing plan",
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied",
        description: "Copied to clipboard",
      });
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        toast({
          title: "Copied",
          description: "Copied to clipboard",
        });
      } catch (fallbackErr) {
        toast({
          title: "Error",
          description: "Unable to copy to clipboard",
          variant: "destructive",
        });
      }
      document.body.removeChild(textArea);
    }
  };

  const goodAsHumanFeatures = [
    "Up to 99% accuracy rate",
    "Process unlimited bills/day",
    "100% scalable. Process 1000s of bills in parallel in under 2 minutes",
    "Generate EDI X12 204 pickup requests",
    "Generate EDI X12 211 bills of lading",
    "SFTP downloads",
    "API downloads",
    "Report missing data",
  ];

  const betterThanHumanFeatures = [
    "Up to 99.9% accuracy rate",
    "Process unlimited bills/day",
    "100% scalable. Process 1000s of bills in parallel in under 2 minutes",
    "Generate EDI X12 204 pickup requests",
    "Generate EDI X12 211 bills of lading",
    "SFTP downloads with download logs",
    "API downloads",
    "Report missing data",
    "Custom ISA sender IDs",
    "Custom extraction for shipment PRO numbers",
    "Advanced AI features",
    "Extract data from barcodes and QR codes",
    "Optional Microsoft Azure billing",
  ];

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Settings</h1>
      </div>

      {/* API Keys Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            API Keys
          </CardTitle>
          <CardDescription>
            Create and manage API keys for server-to-server access.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="newKeyName">Key name</Label>
            <div className="flex gap-2">
              <Input
                id="newKeyName"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g. ERP Integration"
              />
              <Button onClick={handleCreateApiKey} disabled={creatingKey}>
                {creatingKey ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  "Create"
                )}
              </Button>
            </div>
            {justCreatedKey && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                <div className="text-sm font-semibold">
                  Copy your new API key now:
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Input value={justCreatedKey} readOnly />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      copyToClipboard(justCreatedKey!);
                      setJustCreatedKey(null);
                    }}
                  >
                    Copy
                  </Button>
                </div>
                <div className="text-xs text-gray-600 mt-2">
                  For security, the full key will not be shown again.
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <h3 className="text-lg font-semibold">Existing Keys</h3>
            <div className="space-y-2">
              {apiKeys.length === 0 && (
                <div className="text-sm text-gray-500">No API keys yet.</div>
              )}
              {apiKeys.map((k) => (
                <div
                  key={k.id}
                  className="flex items-center justify-between p-3 border rounded"
                >
                  <div>
                    <div className="font-medium">{k.name}</div>
                    <div className="text-xs text-gray-500">
                      Prefix: {k.keyPrefix} • Created:{" "}
                      {new Date(k.createdAt).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      Status: {k.isActive ? "Active" : "Revoked"}
                      {k.lastUsedAt
                        ? ` • Last used: ${new Date(
                            k.lastUsedAt
                          ).toLocaleString()}`
                        : ""}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRotateApiKey(k.id)}
                      disabled={!k.isActive}
                    >
                      Rotate
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleRevokeApiKey(k.id)}
                      disabled={!k.isActive}
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SFTP Credentials Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            SFTP Login Credentials
          </CardTitle>
          <CardDescription>
            Your SFTP credentials for accessing EDI files
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!sftpCredentials ? (
            /* No SFTP User - Show Creation Interface */
            <div className="text-center py-8">
              <Server className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">No SFTP Account</h3>
              <p className="text-gray-600 mb-6">
                You need to create an SFTP account to access your EDI files
              </p>

              <Button
                onClick={handleCreateSftpUser}
                disabled={creatingUser}
                size="lg"
              >
                {creatingUser ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4 mr-2" />
                )}
                Create SFTP Account
              </Button>
            </div>
          ) : (
            /* SFTP User Exists - Show Credentials */
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="username"
                      value={sftpCredentials.username}
                      readOnly
                      className="bg-gray-50"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(sftpCredentials.username)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="server">FTP Server Address</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="server"
                      value={sftpCredentials.server}
                      readOnly
                      className="bg-gray-50"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(sftpCredentials.server)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="port">Port</Label>
                  <Input
                    id="port"
                    value={sftpCredentials.port.toString()}
                    readOnly
                    className="bg-gray-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Current Password</Label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={sftpCredentials.password}
                        readOnly
                        className="bg-gray-50 pr-10"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(sftpCredentials.password)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Account Status */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  {sftpCredentials.isActive ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <Badge variant="outline" className="bg-green-50">
                        Active
                      </Badge>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      <Badge variant="outline" className="bg-red-50">
                        Inactive
                      </Badge>
                    </>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  Created:{" "}
                  {new Date(sftpCredentials.createdAt).toLocaleDateString()}
                  {sftpCredentials.lastLogin && (
                    <span className="ml-2">
                      Last login:{" "}
                      {new Date(sftpCredentials.lastLogin).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              {/* Password Update Section */}
              <div className="pt-4 border-t">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Update Password
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password (min 10 characters)"
                        className="pr-10"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Password must be at least 10 characters with uppercase,
                      lowercase, and numbers
                    </p>
                  </div>
                  <Button
                    onClick={handleUpdatePassword}
                    disabled={
                      updating || !newPassword.trim() || newPassword.length < 10
                    }
                    className="w-full md:w-auto"
                  >
                    {updating ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Key className="h-4 w-4 mr-2" />
                    )}
                    Update Password
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* EDI Document Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            EDI Document Settings
          </CardTitle>
          <CardDescription>
            Configure which EDI document types to generate and X12 version
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Document Types</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edi204"
                  checked={userSettings.ediDocumentTypes.EDI204}
                  onChange={(e) =>
                    setUserSettings((prev) => ({
                      ...prev,
                      ediDocumentTypes: {
                        ...prev.ediDocumentTypes,
                        EDI204: e.target.checked,
                      },
                    }))
                  }
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="edi204" className="text-sm font-medium">
                  EDI 204 (Motor Carrier Load Tender)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edi211"
                  checked={userSettings.ediDocumentTypes.EDI211}
                  onChange={(e) =>
                    setUserSettings((prev) => ({
                      ...prev,
                      ediDocumentTypes: {
                        ...prev.ediDocumentTypes,
                        EDI211: e.target.checked,
                      },
                    }))
                  }
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="edi211" className="text-sm font-medium">
                  EDI 211 (Motor Carrier Bill of Lading)
                </Label>
              </div>
              <div className="flex items-center space-x-2 pt-2 border-t border-gray-100">
                <input
                  type="checkbox"
                  id="reviewBeforeEdi"
                  checked={userSettings.reviewBeforeEdiEnabled}
                  onChange={(e) =>
                    setUserSettings((prev) => ({
                      ...prev,
                      reviewBeforeEdiEnabled: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="reviewBeforeEdi" className="text-sm font-medium">
                  Require review before sending EDI (hold jobs for human review
                  before SFTP)
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">X12 Version</h3>
            <select
              value={userSettings.x12Version}
              onChange={(e) =>
                setUserSettings((prev) => ({
                  ...prev,
                  x12Version: e.target.value as "4010" | "5010" | "7010",
                }))
              }
              className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="4010">4010</option>
              <option value="5010">5010</option>
              <option value="7010">7010</option>
            </select>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Timezone</h3>
            <select
              value={userSettings.timezone}
              onChange={(e) =>
                setUserSettings((prev) => ({
                  ...prev,
                  timezone: e.target.value,
                }))
              }
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="America/Phoenix">Arizona Time (MST)</option>
              <option value="America/Anchorage">Alaska Time (AKST)</option>
              <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
              <option value="UTC">UTC</option>
              <option value="Europe/London">London (GMT)</option>
              <option value="Europe/Paris">Paris (CET)</option>
              <option value="Europe/Berlin">Berlin (CET)</option>
              <option value="Asia/Tokyo">Tokyo (JST)</option>
              <option value="Asia/Shanghai">Shanghai (CST)</option>
              <option value="Asia/Kolkata">India (IST)</option>
              <option value="Australia/Sydney">Sydney (AEDT)</option>
            </select>
          </div>

          {/* Removed default interchange ID configuration */}

          <Button
            onClick={handleSaveSettings}
            disabled={updating}
            className="w-full md:w-auto"
          >
            {updating ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <SettingsIcon className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>
        </CardContent>
      </Card>

      {/* Saved Interchange IDs Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Saved Interchange IDs
          </CardTitle>
          <CardDescription>
            Manage labeled Sender and Receiver IDs used in EDI
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Sender IDs</CardTitle>
                <CardDescription>
                  Manage labeled sender interchange IDs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={newSenderLabel}
                    onChange={(e) => setNewSenderLabel(e.target.value)}
                    placeholder="Friendly label"
                  />
                  <Input
                    value={newSenderId}
                    onChange={(e) =>
                      setNewSenderId(e.target.value.slice(0, 15))
                    }
                    placeholder="ID (max 15 chars)"
                    maxLength={15}
                  />
                  <Button onClick={handleAddSenderId}>Add</Button>
                </div>
                <div className="space-y-2">
                  {savedSenderIds.length === 0 && (
                    <div className="text-sm text-gray-500">
                      No saved sender IDs.
                    </div>
                  )}
                  {savedSenderIds.map((s) => (
                    <div
                      key={`${s.label}:${s.interchangeId}`}
                      className="flex items-center justify-between p-3 border rounded"
                    >
                      <div>
                        <div className="font-medium">{`${s.label} [${s.interchangeId}]`}</div>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteSenderId(s.interchangeId)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Receiver IDs</CardTitle>
                <CardDescription>
                  Manage labeled receiver interchange IDs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={newReceiverLabel}
                    onChange={(e) => setNewReceiverLabel(e.target.value)}
                    placeholder="Friendly label"
                  />
                  <Input
                    value={newReceiverId}
                    onChange={(e) =>
                      setNewReceiverId(e.target.value.slice(0, 15))
                    }
                    placeholder="ID (max 15 chars)"
                    maxLength={15}
                  />
                  <Button onClick={handleAddReceiverId}>Add</Button>
                </div>
                <div className="space-y-2">
                  {savedReceiverIds.length === 0 && (
                    <div className="text-sm text-gray-500">
                      No saved receiver IDs.
                    </div>
                  )}
                  {savedReceiverIds.map((r) => (
                    <div
                      key={`${r.label}:${r.interchangeId}`}
                      className="flex items-center justify-between p-3 border rounded"
                    >
                      <div>
                        <div className="font-medium">{`${r.label} [${r.interchangeId}]`}</div>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteReceiverId(r.interchangeId)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Plan Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5" />
            Pricing Plan
          </CardTitle>
          <CardDescription>
            Choose your processing plan and view included features
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Good as Human Plan */}
            <div
              className={`relative border-2 rounded-lg p-6 cursor-pointer transition-all ${
                pricingPlan === "good-as-a-human"
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => setPricingPlan("good-as-a-human")}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold">Good as a Human</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">8</span>
                    <span className="text-gray-600 ml-2">
                      credits per document
                    </span>
                  </div>
                </div>
                <input
                  type="radio"
                  name="pricingPlan"
                  value="good-as-a-human"
                  checked={pricingPlan === "good-as-a-human"}
                  onChange={() => setPricingPlan("good-as-a-human")}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
              </div>
              <p className="text-gray-600 mb-4">
                Perfect for small to medium-sized businesses with occasional
                bill of lading processing needs.
              </p>
              <ul className="space-y-2">
                {goodAsHumanFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Better than Human Plan */}
            <div
              className={`relative border-2 rounded-lg p-6 cursor-pointer transition-all ${
                pricingPlan === "better-than-a-human"
                  ? "border-purple-500 bg-purple-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => setPricingPlan("better-than-a-human")}
            >
              <Badge className="absolute top-4 right-4 bg-purple-600 text-white">
                RECOMMENDED
              </Badge>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold">Better than a Human</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">10</span>
                    <span className="text-gray-600 ml-2">
                      credits per document
                    </span>
                  </div>
                </div>
                <input
                  type="radio"
                  name="pricingPlan"
                  value="better-than-a-human"
                  checked={pricingPlan === "better-than-a-human"}
                  onChange={() => setPricingPlan("better-than-a-human")}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
              </div>
              <p className="text-gray-600 mb-4">
                Perfect for high-volume logistics operations and enterprise
                customers. Includes advanced AI features and premium support.
              </p>
              <ul className="space-y-2">
                {betterThanHumanFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <Button
            onClick={handleSavePricingPlan}
            disabled={updating}
            className="w-full md:w-auto"
          >
            {updating ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Crown className="h-4 w-4 mr-2" />
            )}
            Save Pricing Plan
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
